import type {ColorMap, CSS} from '@pinecast/styles';
import styled, {focusRing} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import type {NilableLocalizedReactNode} from './types';

/** The shadows that draw the circle of a checked radio. */
export const checkedRadioShadow = (designColors: ColorMap) =>
  `inset 0 0 0 0 ${designColors.borderGrayAccent}, inset 0 0 0 5px ${designColors.accentBlue}, 0 0 0 0 ${designColors.accentBlueLight}`;

const WrapperLabel = styled(
  'label',
  {
    display: 'flex',
    padding: '0 0 20px',

    ':not(:empty) ~ .Radio--WrapperLabel': {
      marginTop: -8,
    },
  },
  {className: 'Radio--WrapperLabel Label--NativeLabel'},
);
const InvisibleRadio = styled(
  'input',
  (_, designColors) => ({
    border: 0,
    display: 'block',
    flex: '0 0',
    height: 0,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    opacity: 0,
    padding: 0,
    width: 0,

    ':checked ~ .Radio-text::before': {
      boxShadow: checkedRadioShadow(designColors),
    },
    // The input is invisible, so the circle it checks shows the ring, outside
    // it: checked, the circle is the ring's own color in light mode.
    ':focus-visible ~ .Radio-text::before': focusRing(designColors),
  }),
  {className: 'Radio--InvisibleRadio', type: 'radio'},
);

/**
 * The text of a radio, and its circle, drawn before the text. The circle is
 * unchecked: the input's `:checked` state draws it checked. An option that
 * draws a radio with no input of its own (`OptionButtonWithRadio`) uses
 * `checkedRadioShadow` for that instead.
 */
export const radioTextStyles = (
  {
    $alignInput,
    $disabled,
  }: {
    $alignInput: 'top' | 'center';
    $disabled: boolean;
  },
  designColors: ColorMap,
): CSS => ({
  alignItems: 'center',
  display: 'block',
  // The medium weight is its own family here: `DEFAULT_FONT` has only a 400
  // face, so a 500 on it renders the same as regular text, as `Checkbox` does.
  fontFamily: `"Monument Grotesk Bold", ${DEFAULT_FONT}`,
  fontSize: 14,
  fontWeight: 500,
  flex: '1 1',
  opacity: $disabled ? 0.5 : 1,
  paddingLeft: 24,
  position: 'relative',
  transition: 'opacity 0.2s',

  '::before': {
    backgroundColor: '#fff',
    borderRadius: '100%',
    bottom: 0,
    boxShadow: `inset 0 0 0 1px ${designColors.borderGrayAccent}, inset 0 0 0 0 ${designColors.accentBlue}, 0 0 0 0 ${designColors.accentBlueLight}`,
    content: '""',
    height: 16,
    left: 0,
    margin: $alignInput === 'top' ? '0 auto auto' : 'auto',
    position: 'absolute',
    top: $alignInput === 'top' ? 1 : 0,
    transition: 'box-shadow 0.2s',
    width: 16,
    zIndex: 1,
  },
});

const Text = styled('div', radioTextStyles, {className: 'Radio-text'});

// The description under the text, in the regular face that the `subText` of a
// `Label` has, with a small gap above it. `Checkbox` draws its own the same way.
const SubText = styled('span', {
  display: 'block',
  fontFamily: DEFAULT_FONT,
  fontWeight: 400,
  marginTop: 4,
});

const Radio = ({
  alignInput = 'center',
  checked,
  disabled,
  hideFromScreenReaders,
  name,
  onChange,
  style,
  subText,
  tabIndex,
  text,
}: {
  alignInput?: 'top' | 'center';
  checked: boolean;
  disabled?: boolean;
  hideFromScreenReaders?: boolean;
  name?: string;
  onChange: (checkedValue: boolean) => void;
  style?: CSS;
  /**
   * A description under the text, in a lighter face. It describes the radio
   * (`aria-describedby`) and is not part of its name, so a screen reader says
   * the text first and the description after it. Put what the choice is in
   * `text`, and what it does or why in here.
   */
  subText?: NilableLocalizedReactNode;
  tabIndex?: number;
  text: React.ReactNode;
}) => {
  const id = React.useId();
  const textID = `${id}-text`;
  const subTextID = `${id}-subtext`;
  return (
    <WrapperLabel style={style}>
      <InvisibleRadio
        // The label wraps the description too, so it would be in the name.
        // Naming the radio by the text's own element keeps it out.
        aria-describedby={subText ? subTextID : undefined}
        aria-hidden={hideFromScreenReaders}
        aria-labelledby={subText ? textID : undefined}
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.checked)
        }
        tabIndex={tabIndex}
      />
      <Text $alignInput={alignInput} $disabled={disabled || false}>
        {subText ? (
          <>
            <span id={textID} style={{display: 'block'}}>
              {text}
            </span>
            <SubText id={subTextID}>{subText}</SubText>
          </>
        ) : (
          text
        )}
      </Text>
    </WrapperLabel>
  );
};

export default Radio;
